import { useEffect, useState } from "react";
import useCurrentGame from "../../hooks/useCurrentGame";
import INTOUCH_API from "../../services/Api";
import Game from "../../store/Game";
import Swiper from "../../store/Swiper";
import useCurrentUserData from "../../hooks/useCurrentUserData";
import useAuth from "../../hooks/useAuth";
import { TEMPORARY_ID_KEY } from "../../utils/constants";
import useBrowserStorage from "../../hooks/useBrowserStorage";
import "./style.scss";

const typesAnswers = {
  positive: 1,
  neutral: 2,
  negative: 3,
};

export default function useViewController(onFeedbackSwipe) {
  const { getCurrentGame } = useCurrentGame();
  const { token } = useAuth();
  const { getItem } = useBrowserStorage();
  const { getUserData } = useCurrentUserData();

  // modal mobile controller
  const [isOpen, setIsOpen] = useState(false);
  const [isSave, setIsSave] = useState(false);
  // for display the current card
  const [countCard, setCountCard] = useState(0);
  // for display progress the current card
  const [progressCountCard, setProgressCountCard] = useState(0);

  const [completedCards, setCompletedCards] = useState([]);
  const [completedCardsEditSession, setCompletedCardsEditSession] = useState(
    []
  );
  const [isSwiping, setIsSwiping] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const onSave = () => {
    if (!selectedCard) return;
    const answer = Object.values(typesAnswers).find(
      (ans) => ans === selectedCard.answer
    );
    const answeredCard = { answer, id: selectedCard.id };
    onFeedbackSwipe(answeredCard);
    setIsSave(true);
  };

  const incrementProgressCountCard = () => {
    setProgressCountCard(progressCountCard + 1);
  };
  const incrementCountCard = () => {
    setCountCard(countCard + 1);
  };
  const decrementCountCard = () => {
    if (countCard === 0) return;
    setCountCard((prevState) => prevState - 1);
  };
  const setCompleteCards = (card, action) => {
    const answeredCard = completedCards.find(
      (completedCard) =>
        (completedCard?.questionId || completedCard?.id) === card?.id
    );
    if (action) {
      setCompletedCardsEditSession((prevState) => {
        if (action === "negative") {
          return prevState.slice(0, prevState.length - 1);
        } else {
          return [...prevState, card];
        }
      });
      return;
    }

    if (answeredCard) {
      updateCard(answeredCard, card);
      return;
    }
    setCompletedCards((prevState) => {
      incrementProgressCountCard();
      prevState[countCard] = card;
      return [...prevState];
    });
  };
  const updateCard = (answeredCard, card) => {
    const updatedCards = completedCards.map((completedCard) => {
      setIsSave(false);
      const isTheSameCard =
        (completedCard?.questionId || completedCard?.id) ===
        (answeredCard?.questionId || answeredCard?.id);
      if (isTheSameCard) {
        if (Swiper.isPositive || Swiper.isToRight) {
          completedCard = { ...answeredCard, answer: 1 };
        }
        if (Swiper.isNegative || Swiper.isToLeft) {
          completedCard = { ...answeredCard, answer: 3 };
        }
        if (Swiper.isNeutral || Swiper.isToTop) {
          completedCard = { ...answeredCard, answer: 2 };
        }
      }
      return completedCard;
    });
    return setCompletedCards(updatedCards);
  };

  const isAnsweredCardsExist = (swipeCard) => {
    if (!completedCards.length) return;
    const currentCard = completedCards.find((card) => {
      return swipeCard?.id === (card?.questionId || card?.id);
    });
    return !currentCard;
  };
  const calculatePercentProgress = () => {
    if (!completedCardsEditSession.length) return 0;
    const answeredCards = completedCardsEditSession.filter(
      (card) => card?.answer > 0
    );
    return (answeredCards.length / completedCards.length) * 100;
  };

  const continueSwipe = (card, stepX, stepY, endSwipe) => {
    if (Swiper.isContinueSwiping) return;
    Swiper.setIsContinueSwiping(true);
    const stepRotate = 1;
    const positiveSwipeContinueID = setInterval(() => {
      if (
        Swiper.transform.x <= -250 ||
        Swiper.transform.x >= 250 ||
        Swiper.transform.y < -450
      ) {
        sendAnswerBySwipe(card);
        clearInterval(positiveSwipeContinueID);
        endSwipe();
        preventCardState();
      }
      if (!stepX) {
        Swiper.setTransform({
          ...Swiper.transform,
          y: Swiper.transform.y + stepY,
        });
      } else {
        Swiper.setTransform({
          x: (Swiper.transform.x + stepX) * Math.cos(19),
          y: (Swiper.transform.y + stepY) * Math.sin(19),
          rotate: `${(Swiper.transform.x + stepRotate) / 12}`,
        });
      }
    });
  };

  const preventCardState = () => {
    Swiper.setTransform({ x: 0, y: 0, rotate: 0 });
    Swiper.setOpacityCard(1);
    Swiper.setIsContinueSwiping(false);
  };

  const onSwipeEnd = (event, card) => {
    setIsSave(false);
    if (Swiper.isToLeft) {
      continueSwipe(card, -5, -5, () => {
        Swiper.setIsToLeft(false);
      });
    }
    if (Swiper.isToRight) {
      continueSwipe(card, 5, 5, () => {
        Swiper.setIsToRight(false);
      });
    }
    if (Swiper.isToTop) {
      continueSwipe(card, null, -5, () => {
        Swiper.setIsToTop(false);
      });
    }

    setIsSwiping(false);
    if (!Swiper.isToTop && !Swiper.isToLeft && !Swiper.isToRight) {
      preventCardState();
    }
  };

  const positiveSwipe = (card) => {
    if (Swiper.isPositive) return;
    Swiper.setIsPositive(true);
    const answeredCard = { answer: typesAnswers.positive, id: card.id };
    setSelectedCard(answeredCard);
    setCompleteCards(answeredCard);
    Swiper.setIsPositive(false);
  };

  const negativeSwipe = (card) => {
    if (Swiper.isNegative) return;
    Swiper.setIsNegative(true);
    const answeredCard = { answer: typesAnswers.negative, id: card.id };
    setSelectedCard(answeredCard);
    setCompleteCards(answeredCard);
    Swiper.setIsNegative(false);
  };

  const neutralSwipe = (card) => {
    if (Swiper.isNeutral) return;
    Swiper.setIsNeutral(true);
    const answeredCard = { answer: typesAnswers.neutral, id: card.id };
    setSelectedCard(answeredCard);
    setCompleteCards(answeredCard);
    Swiper.setIsNeutral(false);
  };

  const forwardSwipe = (card) => {
    if (countCard === Game.cards.length - 1) return;
    if (!completedCards.length) return;
    setIsSwiping(true);
    incrementCountCard();
    const answeredCard = { answer: typesAnswers.positive, id: card.id };
    setCompleteCards(answeredCard, "forward");
    setSelectedCard(null);
    setIsSwiping(false);
    setIsSave(false);
  };
  const backSwipe = (card) => {
    if (countCard === 0) return;
    setIsSwiping(true);
    decrementCountCard();
    const answeredCard = { answer: typesAnswers.positive, id: card.id };
    setCompleteCards(answeredCard, "negative");
    setSelectedCard(null);
    setIsSwiping(false);
    setIsSave(false);
  };
  const sendAnswerBySwipe = (card) => {
    const answeredCard = getAnswer(card);
    if (!answeredCard) return;
    // first send the answer to the server and after show next card
    if (Swiper.isToRight) {
      incrementCountCard();
      setCompleteCards(answeredCard, "forward");
    }
    if (Swiper.isToLeft) {
      decrementCountCard();
      setCompleteCards(answeredCard, "negative");
    }
  };
  const getAnswer = (card) => {
    if (Swiper.isToRight) {
      return { answer: typesAnswers.positive, id: card.id };
    }
    if (Swiper.isToLeft) {
      return { answer: typesAnswers.negative, id: card.id };
    }
    if (Swiper.isToTop) {
      return { answer: typesAnswers.neutral, id: card.id };
    }
  };

  const onSwipeMove = (position, event) => {
    // prevent touch swipe if swiping by button
    if (Swiper.isNeutral || Swiper.isNegative || Swiper.isPositive) return;
    if (Swiper.isContinueSwiping) return;
    setIsSwiping(true);

    // for right swipe
    if (position.x > 70) {
      if (Swiper.isToTop) return;
      Swiper.setIsToRight(true);
    } else {
      Swiper.setIsToRight(false);
    }
    // for left swipe
    if (position.x < -70) {
      if (Swiper.isToTop) return;
      Swiper.setIsToLeft(true);
    } else {
      Swiper.setIsToLeft(false);
    }

    // for left-right swipe
    if (
      position.x <= 250 &&
      position.x > -250 &&
      position.y <= 50 &&
      position.y > -50
    ) {
      Swiper.setTransform({
        x: calculateMovement(position, "x"),
        y: calculateMovement(position, "y"),
        rotate: `${calculateRotate(position, "x")}`,
      });
    }
  };
  const opacityController = (transform) => {
    if (transform.y < -20 && transform.y > -50) {
      Swiper.setOpacityCard(0.7);
    } else if (transform.y < -51 && transform.y > -100) {
      Swiper.setOpacityCard(0.5);
    } else if (transform.y < -101) {
      Swiper.setOpacityCard(0.1);
    }
    if (
      (transform.rotate >= 3 && transform.rotate <= 7) ||
      (transform.rotate <= -3 && transform.rotate >= -7)
    ) {
      Swiper.setOpacityCard(0.7);
    } else if (
      (transform.rotate >= 8 && transform.rotate <= 14) ||
      (transform.rotate <= -8 && transform.rotate >= -14)
    ) {
      Swiper.setOpacityCard(0.5);
    } else if (
      (transform.rotate >= 15 && transform.rotate <= 17) ||
      (transform.rotate <= -15 && transform.rotate >= -17)
    ) {
      Swiper.setOpacityCard(0.3);
    } else if (
      (transform.rotate >= 18 && transform.rotate <= 20) ||
      (transform.rotate <= -18 && transform.rotate >= -20)
    ) {
      Swiper.setOpacityCard(0.1);
    }
  };

  const calculateMovement = (position, axis) => {
    return position[axis] * Math.cos(19);
  };
  const calculateRotate = (position, axis) => {
    return position[axis] / 12;
  };

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    opacityController(Swiper.transform);
  }, [Swiper.transform]);

  const onRestoreGameHistory = async () => {
    const game = getCurrentGame();
    setCompletedCards(new Array(Game.cards.length));
    const temporaryUserId = getItem(TEMPORARY_ID_KEY);
    const currentUser = getUserData();
    const data = {
      gameId: game.id,
    };
    if (token) {
      data.userId = currentUser.id;
    } else {
      data.temporaryUserId = temporaryUserId;
    }
    await INTOUCH_API.requestManager(
      "getHistoryGame",
      data,
      (response) => {
        if (response.data.rows.length) {
          let answeredCards = response.data.rows.filter(
            (card) => card.answer > 0
          );
          setCompletedCards((prevState) => {
            const historyCards = [
              ...answeredCards,
              ...prevState.slice(answeredCards.length, prevState.length),
            ];
            return [...historyCards];
          });
        } else {
          setProgressCountCard(0);
          setCountCard(0);
        }
      },
      () => {}
    );
  };

  useEffect(() => {
    onRestoreGameHistory();
  }, [Game.cards]);

  return {
    incrementCountCard,
    decrementCountCard,
    countCard,
    onSwipeEnd,
    onSwipeMove,
    isToRight: Swiper.isToRight,
    isToLeft: Swiper.isToLeft,
    isToTop: Swiper.isToTop,
    opacityCard: Swiper.opacityCard,
    completedCards,
    isPositive: Swiper.isPositive,
    isNeutral: Swiper.isNeutral,
    isNegative: Swiper.isNegative,
    isSwiping,
    progressCountCard,
    isOpen,
    isSave,
    selectedCard,
    toggleModal,
    isAnsweredCardsExist,
    positiveSwipe,
    negativeSwipe,
    neutralSwipe,
    forwardSwipe,
    backSwipe,
    calculatePercentProgress,
    onRestoreGameHistory,
    onSave,
  };
}
